import useAuth from '@app/auth/use-auth';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import Heading from '@shared/components/content/heading';
import Text from '@shared/components/content/text';
import TextareaFormField from '@shared/components/form/textarea-form-field';
import { Button } from '@shared/components/ui/button';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCCF } from '../../ccf-context';
import { INTENSITY_METRIC_OPTIONS } from '../constants';
import { type SecrFormValues } from '../schema';
import { buildMethodologyText, formatPeriod } from '../secr-report-utils';

interface MethodologySectionProps {
  hasPriorYear: boolean;
}

export function MethodologySection({ hasPriorYear }: MethodologySectionProps) {
  const { startDate, endDate } = useCCF();
  const { user } = useAuth();
  const { control, getValues, setValue } = useFormContext<SecrFormValues>();
  const intensityMetric = useWatch({ control, name: 'intensityMetric' });
  const methodologyValue = useWatch({ control, name: 'methodology' });
  const {
    showSimpleDialog: showConfirm,
    closeSimpleDialog: closeConfirm,
    SimpleDialogComponent: ConfirmRegenerateDialog,
  } = useSimpleDialog();

  const isMetricSelected = intensityMetric != null;
  const isMethodologyEmpty = !methodologyValue?.trim();
  // Lock the textarea only while it's still empty AND no metric is chosen,
  // so users can't write a stub before the auto-generated text has the right
  // intensity clause. Once they have content (generated or saved), let them edit freely.
  const isTextareaLocked = isMethodologyEmpty && !isMetricSelected;
  const isGenerateDisabled = !isMetricSelected;

  const regenerate = () => {
    const values = getValues();
    const option = INTENSITY_METRIC_OPTIONS.find(
      (o) => o.value === values.intensityMetric
    );
    const text = buildMethodologyText({
      organisationName: user?.organizationName ?? 'Organisation',
      period: formatPeriod(startDate, endDate),
      hasPriorYear,
      intensityLabel: option?.label,
      intensityUnit: option?.unit,
    });
    setValue('methodology', text, { shouldDirty: true });
  };

  const handleGenerateClick = () => {
    const current = getValues('methodology')?.trim();
    if (current) {
      showConfirm();
    } else {
      regenerate();
    }
  };

  const handleConfirm = () => {
    regenerate();
    closeConfirm();
  };

  return (
    <div className="space-y-3">
      <div>
        <Heading variant="section">Methodology</Heading>
        <Text variant="subtle" className="mt-1">
          Describe the methodology used to calculate your emissions data,
          including data sources, emission factors, and any assumptions applied.
          Click <span className="font-medium">Generate</span> to build a default
          statement from the form above, then edit as needed. Make sure to
          replace any{' '}
          <span className="font-medium">[BRACKETED PLACEHOLDERS]</span> with
          details specific to your business before publishing.
        </Text>
      </div>
      <TextareaFormField
        name="methodology"
        rows={10}
        disabled={isTextareaLocked}
        placeholder={
          isTextareaLocked
            ? 'Select an intensity metric above, then click Generate to build a methodology statement.'
            : "Click 'Generate' to build a methodology statement from your inputs, then edit as needed."
        }
      />
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerateClick}
          disabled={isGenerateDisabled}
        >
          Generate
        </Button>
      </div>

      {ConfirmRegenerateDialog({
        header: 'Regenerate methodology?',
        content: (
          <p className="text-sm text-muted-foreground">
            This will replace the current methodology statement with a fresh one
            built from the form above. Any edits you have made will be lost. The
            change takes effect when you save.
          </p>
        ),
        footer: (
          <>
            <Button type="button" variant="outline" onClick={closeConfirm}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm}>
              Regenerate
            </Button>
          </>
        ),
      })}
    </div>
  );
}
